/**
 * The authorization vocabulary the panel speaks (§10.3). These are the *only* role strings
 * `GET /auth/me` can return, and they are mirrored from `pkg/access` — adding one here without
 * adding it there (or the reverse) is a bug in one of the two places, never a feature.
 *
 * `user_manager` deliberately grants **no** instance access (§10.3): it may administer users
 * and nothing else, which is why `canManageUsers` and `canReadAllInstances` below are separate
 * questions with separate answers.
 */
export enum Role {
    Admin             = 'admin',
    UserManager       = 'user_manager',
    AllInstancesRead  = 'all_instances:read',
    AllInstancesWrite = 'all_instances:write',
}

/**
 * The effective level `access.Effective` resolved for one instance, as it arrives on every
 * instance-shaped payload (`access_level`, §6.1). The SPA **never recomputes** it from roles
 * and assignments: `pkg/access` is the single authority (§10.3), and a second implementation
 * in TypeScript is exactly the divergence §10.3 exists to prevent.
 */
export enum AccessLevel {
    None  = 'none',
    Read  = 'read',
    Write = 'write',
}

/**
 * The capability slugs `/auth/me` returns, mirrored from `Logic/Access.go`. They are a flat list
 * of strings rather than a struct of booleans so a capability added on the Go side reaches the
 * frontend without a type change on either side.
 *
 * They are **cosmetic only** (§9.6), and the two instance capabilities answer a deliberately
 * coarse question — *"is there at least one instance this user may see / may change"* — derived
 * from the same scopes every list query filters on. That coarseness is the point: it is the one
 * write-ish signal the SPA can read for a **client**-scoped screen without folding `assignments`
 * into an access level itself, which §10.3 forbids because the moment it does, the "why can I do
 * this?" screens can disagree with what enforcement actually did.
 */
export enum Capability {
    ManageUsers    = 'manage_users',
    ManageRoles    = 'manage_roles',
    ReadInstances  = 'read_instances',
    WriteInstances = 'write_instances',
}

/**
 * §8.3's two things a scoped assignment can be attached to, mirrored from `AccessScopeType`
 * (`pkg/enums/AccessScopeType`). A client assignment covers every instance of that client
 * **including ones added later** — resolved against `instance.client_id` at request time, nothing
 * materialised at grant time (§10.3).
 */
export enum AccessScopeType {
    Client   = 'client',
    Instance = 'instance',
}

/**
 * §8.4's three reasons a user has access to an instance, mirrored from `AccessSourceType`
 * (`pkg/enums/AccessSourceType`). Both transparency views — the user access table and the
 * instance access tab — label every source with one of these; `AccessSourceBadges` is the one
 * place that renders them.
 */
export enum AccessSourceType {
    Role               = 'role',
    ClientAssignment   = 'client_assignment',
    InstanceAssignment = 'instance_assignment',
}
